import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { COMING_SOON } from "@/context/CartContext";
import { ArrowRight, ShieldCheck } from "lucide-react";

const LINES = ["BRED IN", "SHADOWS.", "CRAFTED FOR", "THE WILD."];

const lineReveal = {
    hidden: { y: "115%" },
    show: (i) => ({
        y: "0%",
        transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.35 + i * 0.13 },
    }),
};

export const Hero = ({ featured }) => {
    const cardRef = useRef(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 18 });
    const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 18 });
    const imgY = useTransform(my, [-0.5, 0.5], [14, -14]);

    const onMove = (e) => {
        const r = cardRef.current?.getBoundingClientRect();
        if (!r) return;
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    const spotlight = featured?.[0];

    return (
        <section className="relative overflow-hidden pb-24 pt-36 lg:pt-44" data-testid="hero-section">
            <div
                className="pointer-events-none absolute inset-0 opacity-[0.16]"
                style={{
                    backgroundImage:
                        "url(https://images.unsplash.com/photo-1621146027714-e8921770f8d0?crop=entropy&cs=srgb&fm=jpg&q=85)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-obsidian/40 via-obsidian/70 to-obsidian" />
            <div className="pointer-events-none absolute -left-40 top-20 h-[480px] w-[480px] rounded-full bg-red-900/20 blur-[140px]" />
            <div className="pointer-events-none absolute -right-32 bottom-0 h-[380px] w-[380px] rounded-full bg-moss/50 blur-[120px]" />

            <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 sm:px-12 lg:grid-cols-12">
                <div className="lg:col-span-7">
                    <motion.p
                        initial={{ opacity: 0, x: -24 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.15 }}
                        className="mb-8 flex items-center gap-3 font-mono2 text-xs uppercase tracking-[0.35em] text-red-500"
                        data-testid="hero-eyebrow"
                    >
                        <span className="h-px w-10 bg-red-800" />
                        Captive-Bred &amp; Rare Species
                    </motion.p>

                    <motion.p
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.28 }}
                        className="mb-4 font-editorial text-2xl italic text-bone/90 sm:text-3xl"
                        data-testid="hero-company-title"
                    >
                        Logan's Reptiles &amp; Exotics
                    </motion.p>

                    <h1
                        data-testid="hero-title"
                        className="font-display text-4xl font-extrabold leading-[0.95] tracking-tight text-bone sm:text-5xl lg:text-7xl"
                    >
                        {LINES.map((line, i) => (
                            <span key={line} className="block overflow-hidden pb-1">
                                <motion.span
                                    custom={i}
                                    variants={lineReveal}
                                    initial="hidden"
                                    animate="show"
                                    className={`block ${i % 2 === 1 ? "font-editorial italic text-red-500" : ""}`}
                                >
                                    {line}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1.1 }}
                        className="mt-8 max-w-xl text-base leading-relaxed text-ash sm:text-lg"
                    >
                        Rare captive-bred serpents, lizards, spiders, frogs and exotic mammals —
                        raised in the dark heart of our jungle facility. Every specimen documented,
                        every bloodline known.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1.3 }}
                        className="mt-10 flex flex-wrap items-center gap-5"
                    >
                        <Link
                            to="/shop"
                            data-testid="hero-cta-shop"
                            className="group flex items-center gap-3 bg-crimson px-8 py-4 font-mono2 text-xs uppercase tracking-[0.25em] text-bone transition-colors duration-300 hover:bg-red-700"
                        >
                            Enter the Shop
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1.6 }}
                        className="mt-12 flex flex-wrap gap-8 font-mono2 text-[10px] uppercase tracking-[0.2em] text-ash/70"
                    >
                        <span className="flex items-center gap-2">
                            <ShieldCheck className="h-3.5 w-3.5 text-red-500" /> Live Arrival Guarantee
                        </span>
                        <span className="flex items-center gap-2">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            Overnight Air Freight
                        </span>
                        <span className="flex items-center gap-2">
                            <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
                            100% Captive Bred
                        </span>
                    </motion.div>
                </div>

                <div className="lg:col-span-5">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.92 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.1, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
                        style={{ perspective: 1000 }}
                        className="relative"
                    >
                        <motion.div
                            ref={cardRef}
                            onMouseMove={onMove}
                            onMouseLeave={() => {
                                mx.set(0);
                                my.set(0);
                            }}
                            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
                            className="spotlight-card relative border border-red-900/50 bg-coal"
                            data-testid="hero-spotlight-card"
                        >
                            {spotlight && !COMING_SOON && (
                                <>
                                    <div className="relative overflow-hidden" style={{ clipPath: "polygon(0 0, 100% 0, 100% 92%, 92% 100%, 0 100%)" }}>
                                        <motion.img
                                            src={spotlight.image_url}
                                            alt={spotlight.name}
                                            style={{ y: imgY }}
                                            className="h-[420px] w-full scale-110 object-cover"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-black/30" />
                                    </div>
                                    <div className="absolute bottom-0 left-0 right-0 p-6" style={{ transform: "translateZ(40px)" }}>
                                        <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-red-400">
                                            Spotlight Specimen
                                        </p>
                                        <p className="mt-2 font-display text-2xl font-bold text-bone">{spotlight.name}</p>
                                        <p className="font-editorial text-lg italic text-ash">{spotlight.species}</p>
                                        <div className="mt-3 flex items-center justify-between">
                                            <span className="font-mono2 text-sm text-bone">
                                                ${spotlight.price.toLocaleString()}
                                            </span>
                                            <span className="border border-emerald-800/60 bg-emerald-950/40 px-2 py-1 font-mono2 text-[9px] uppercase tracking-[0.2em] text-emerald-400">
                                                {spotlight.stock} available
                                            </span>
                                        </div>
                                    </div>
                                    <span className="absolute right-4 top-4 border border-red-700/70 bg-black/70 px-3 py-1 font-mono2 text-[9px] uppercase tracking-[0.25em] text-red-400 backdrop-blur-sm animate-pulse-glow">
                                        {spotlight.rarity}
                                    </span>
                                </>
                            )}
                        </motion.div>
                        <div className="pointer-events-none absolute -bottom-4 -right-4 -z-10 h-full w-full border border-moss" />
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
